import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Truck, Check } from "lucide-react";

/**
 * Live "Add $X.XX more for free shipping" progress indicator.
 * Pulls the threshold from /api/shop-status (source of truth in backend .env).
 * Renders nothing until the threshold is known.
 *
 * Props:
 *   subtotal   number — current cart subtotal
 *   compact    bool   — slimmer styling for the cart drawer
 */
const ShippingProgress = ({ subtotal, compact = false }) => {
    const [threshold, setThreshold] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/shop-status")
            .then((r) => {
                if (!alive) return;
                const t = r.data?.shipping?.free_threshold_usd;
                if (typeof t === "number" && t > 0) setThreshold(t);
            })
            .catch(() => {
                /* silently skip — module is non-critical */
            });
        return () => {
            alive = false;
        };
    }, []);

    if (!threshold || subtotal <= 0) return null;

    const qualifies = subtotal >= threshold;
    const remaining = Math.max(0, threshold - subtotal);
    const pct = Math.min(100, Math.round((subtotal / threshold) * 100));

    if (qualifies) {
        return (
            <div
                data-testid="shipping-progress-qualified"
                className={`flex items-center gap-2 ${compact ? "text-xs" : "text-sm"} text-[#22c55e] font-chivo tracking-[0.04em] py-2`}
            >
                <Check className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
                <span>You qualify for FREE shipping.</span>
            </div>
        );
    }

    return (
        <div
            data-testid="shipping-progress-bar"
            className={`${compact ? "py-2" : "py-3"}`}
        >
            <div
                className={`flex items-center gap-2 mb-2 ${compact ? "text-xs" : "text-sm"} font-chivo tracking-[0.04em] text-slate-300`}
            >
                <Truck className={compact ? "w-3.5 h-3.5 text-[#004466]" : "w-4 h-4 text-[#004466]"} />
                <span>
                    Add{" "}
                    <span className="font-anton text-white">
                        ${remaining.toFixed(2)}
                    </span>{" "}
                    more for{" "}
                    <span className="text-[#004466] font-semibold">FREE shipping</span>
                </span>
            </div>
            <div
                className={`relative w-full ${compact ? "h-1" : "h-1.5"} bg-[#1E293B] overflow-hidden`}
                aria-label={`${pct}% of free shipping threshold reached`}
            >
                <div
                    className="absolute inset-y-0 left-0 bg-[#004466] transition-[width] duration-500 ease-out"
                    style={{ width: `${pct}%` }}
                    data-testid="shipping-progress-fill"
                />
            </div>
        </div>
    );
};

export default ShippingProgress;
